/**
 * Shared error message translations for all monorepo projects
 *
 * Usage in your app:
 *
 * import { ERROR_TRANSLATIONS } from '@shared/i18n/error-translations';
 *
 * // In your transloco config or preload:
 * // Merge these translations with your app-specific translations
 *
 * const enTranslations = {
 *   ...yourAppTranslations,
 *   ...ERROR_TRANSLATIONS.en
 * };
 *
 * // Then use in components:
 * this.translocoService.translate('errors.jobApply.JOB_ALREADY_APPLIED.title')
 * // Or in templates:
 * {{ 'errors.jobApply.JOB_ALREADY_APPLIED.message' | transloco }}
 */

const errorsEn = {
  "errors": {
    "jobApply": {
      "JOB_ALREADY_APPLIED": {
        "title": "Already Applied",
        "message": "You have already applied for this job. You cannot apply multiple times for the same position."
      },
      "JOB_OFFER_NOT_FOUND": {
        "title": "Job Not Found",
        "message": "The job offer you are trying to apply for no longer exists or has been removed."
      },
      "JOB_ALREADY_ASSIGNED_TO_CANDIDATE": {
        "title": "Already Assigned",
        "message": "This job has already been assigned to you. Please check your applications."
      },
      "NETWORK_ERROR": {
        "title": "Network Error",
        "message": "Unable to connect to the server. Please check your internet connection and try again."
      },
      "INVALID_DATA": {
        "title": "Invalid Data",
        "message": "The information you provided is invalid. Please check your inputs and try again."
      },
      "UNAUTHORIZED": {
        "title": "Unauthorized",
        "message": "You are not authorized to perform this action. Please sign in and try again."
      },
      "INTERNAL_SERVER_ERROR": {
        "title": "Server Error",
        "message": "An unexpected error occurred on the server. Please try again later."
      },
      "UNKNOWN_ERROR": {
        "title": "Error Occurred",
        "message": "An unexpected error occurred. Please try again or contact support if the problem persists."
      }
    },
    "common": {
      "CONNECTION_ERROR": {
        "title": "Connection Error",
        "message": "Please check your internet connection."
      },
      "SESSION_EXPIRED": {
        "title": "Session Expired",
        "message": "Please log in again."
      },
      "GENERIC_ERROR": {
        "title": "Error",
        "message": "An error occurred. Please try again."
      }
    },
    "jobOperations": {
      "SAVE_JOB_FAILED": {
        "title": "Error",
        "message": "Failed to save job. Please try again."
      },
      "UNSAVE_JOB_FAILED": {
        "title": "Error",
        "message": "Failed to unsave job. Please try again."
      },
      "LOAD_JOBS_FAILED": {
        "title": "Error Loading Jobs",
        "message": "Sorry, we couldn't load the job listings. Please try again later."
      },
      "LOAD_SAVED_JOBS_FAILED": {
        "title": "Error Loading Saved Jobs",
        "message": "Failed to load saved jobs. Please try again."
      },
      "JOB_NOT_FOUND": {
        "title": "Job Not Found",
        "message": "Sorry, we couldn't find the job you're looking for. It may have been removed or doesn't exist."
      },
      "SAVE_JOB_AUTH_REQUIRED": {
        "title": "Login Required",
        "message": "You need to log in to save jobs to your profile. Please sign in or create an account to continue."
      }
    },
    "auth": {
      "INVALID_CREDENTIALS": {
        "title": "Invalid Credentials",
        "message": "Invalid email or password"
      },
      "LOGIN_FAILED": {
        "title": "Login Failed",
        "message": "An error occurred during login"
      },
      "REGISTRATION_FAILED": {
        "title": "Registration Failed",
        "message": "An error occurred during registration. Please try again later."
      },
      "INVALID_RESET_TOKEN": {
        "title": "Invalid Token",
        "message": "Invalid or expired token. Please request a new password reset link."
      },
      "NO_RESET_TOKEN": {
        "title": "No Token",
        "message": "No reset token provided. Please request a new password reset link."
      },
      "PASSWORD_RESET_FAILED": {
        "title": "Reset Failed",
        "message": "An error occurred while resetting your password. Please try again."
      }
    },
    "profile": {
      "UPDATE_FAILED": {
        "title": "Update Failed",
        "message": "There was a problem updating your profile. Please try again."
      }
    },
    "resumeParsing": {
      "PARSING_FAILED": {
        "title": "Parsing Failed",
        "message": "We couldn't process your resume. Please try again."
      },
      "INVALID_FILE": {
        "title": "Invalid File",
        "message": "Invalid file format"
      },
      "FILE_TOO_LARGE": {
        "title": "File Too Large",
        "message": "File is too large"
      },
      "PROCESSING_ERROR": {
        "title": "Processing Error",
        "message": "Processing error occurred"
      },
      "NETWORK_ERROR": {
        "title": "Network Error",
        "message": "Network connection issue"
      },
      "SERVER_ERROR": {
        "title": "Server Error",
        "message": "Server error occurred"
      }
    },
    "accountDeletion": {
      "LOAD_REQUESTS_FAILED": {
        "title": "Error",
        "message": "Failed to load deletion requests. Please try again."
      },
      "CREATE_REQUEST_FAILED": {
        "title": "Error",
        "message": "Failed to create deletion request. Please try again."
      },
      "CANCEL_REQUEST_FAILED": {
        "title": "Error",
        "message": "Failed to cancel deletion request. Please try again."
      },
      "DUPLICATE_REQUEST": {
        "title": "Duplicate Request",
        "message": "A deletion request already exists for this entity."
      },
      "INVALID_PASSWORD": {
        "title": "Invalid Password",
        "message": "The password you entered is incorrect."
      },
      "BAD_REQUEST": {
        "title": "Bad Request",
        "message": "The request could not be processed. Please check your input and try again."
      }
    }
  }
};

const errorsSk = {
  "errors": {
    "jobApply": {
      "JOB_ALREADY_APPLIED": {
        "title": "Už ste sa prihlásili",
        "message": "Na túto pozíciu ste sa už prihlásili. Nemôžete sa prihlásiť viackrát na rovnakú pozíciu."
      },
      "JOB_OFFER_NOT_FOUND": {
        "title": "Pracovná ponuka nenájdená",
        "message": "Pracovná ponuka, o ktorú sa snažíte uchádzať, už neexistuje alebo bola odstránená."
      },
      "JOB_ALREADY_ASSIGNED_TO_CANDIDATE": {
        "title": "Už priradené",
        "message": "Táto práca vám už bola priradená. Skontrolujte prosím vaše prihlášky."
      },
      "NETWORK_ERROR": {
        "title": "Chyba siete",
        "message": "Nepodarilo sa pripojiť k serveru. Skontrolujte prosím svoje internetové pripojenie a skúste to znova."
      },
      "INVALID_DATA": {
        "title": "Neplatné údaje",
        "message": "Poskytnuté informácie sú neplatné. Skontrolujte prosím svoje vstupy a skúste to znova."
      },
      "UNAUTHORIZED": {
        "title": "Neautorizovaný prístup",
        "message": "Nemáte oprávnenie vykonať túto akciu. Prihláste sa prosím a skúste to znova."
      },
      "INTERNAL_SERVER_ERROR": {
        "title": "Chyba servera",
        "message": "Na serveri došlo k neočakávanej chybe. Skúste to prosím neskôr."
      },
      "UNKNOWN_ERROR": {
        "title": "Nastala chyba",
        "message": "Došlo k neočakávanej chybe. Skúste to prosím znova alebo kontaktujte podporu, ak problém pretrváva."
      }
    },
    "common": {
      "CONNECTION_ERROR": {
        "title": "Chyba pripojenia",
        "message": "Skontrolujte prosím svoje internetové pripojenie."
      },
      "SESSION_EXPIRED": {
        "title": "Relácia vypršala",
        "message": "Prihláste sa prosím znova."
      },
      "GENERIC_ERROR": {
        "title": "Chyba",
        "message": "Nastala chyba. Skúste to prosím znova."
      }
    },
    "jobOperations": {
      "SAVE_JOB_FAILED": {
        "title": "Chyba",
        "message": "Nepodarilo sa uložiť prácu. Skúste to prosím znova."
      },
      "UNSAVE_JOB_FAILED": {
        "title": "Chyba",
        "message": "Nepodarilo sa odstrániť uloženú prácu. Skúste to prosím znova."
      },
      "LOAD_JOBS_FAILED": {
        "title": "Chyba pri načítavaní pracovných ponúk",
        "message": "Ľutujeme, nemohli sme načítať pracovné ponuky. Skúste to prosím neskôr."
      },
      "LOAD_SAVED_JOBS_FAILED": {
        "title": "Chyba pri načítavaní uložených pracovných ponúk",
        "message": "Nepodarilo sa načítať uložené práce. Skúste to prosím znova."
      },
      "JOB_NOT_FOUND": {
        "title": "Pracovná ponuka nenájdená",
        "message": "Ľutujeme, nemohli sme nájsť pracovnú ponuku, ktorú hľadáte. Mohla byť odstránená alebo neexistuje."
      },
      "SAVE_JOB_AUTH_REQUIRED": {
        "title": "Vyžaduje sa prihlásenie",
        "message": "Musíte sa prihlásiť, aby ste mohli uložiť prácu do svojho profilu. Prihláste sa prosím alebo vytvorte účet."
      }
    },
    "auth": {
      "INVALID_CREDENTIALS": {
        "title": "Neplatné prihlasovacie údaje",
        "message": "Neplatný e-mail alebo heslo"
      },
      "LOGIN_FAILED": {
        "title": "Prihlásenie zlyhalo",
        "message": "Vyskytla sa chyba pri prihlasovaní"
      },
      "REGISTRATION_FAILED": {
        "title": "Registrácia zlyhala",
        "message": "Vyskytla sa chyba pri registrácii. Skúste to prosím neskôr."
      },
      "INVALID_RESET_TOKEN": {
        "title": "Neplatný token",
        "message": "Neplatný alebo expirovaný token. Požiadajte prosím o nový odkaz na obnovenie hesla."
      },
      "NO_RESET_TOKEN": {
        "title": "Žiadny token",
        "message": "Nebol poskytnutý token na obnovenie. Požiadajte prosím o nový odkaz na obnovenie hesla."
      },
      "PASSWORD_RESET_FAILED": {
        "title": "Obnovenie zlyhalo",
        "message": "Vyskytla sa chyba pri obnovovaní hesla. Skúste to prosím znova."
      }
    },
    "profile": {
      "UPDATE_FAILED": {
        "title": "Aktualizácia zlyhala",
        "message": "Vyskytol sa problém pri aktualizácii vášho profilu. Skúste to prosím znova."
      }
    },
    "resumeParsing": {
      "PARSING_FAILED": {
        "title": "Analýza zlyhala",
        "message": "Nemohli sme spracovať váš životopis. Skúste to prosím znova."
      },
      "INVALID_FILE": {
        "title": "Neplatný súbor",
        "message": "Neplatný formát súboru"
      },
      "FILE_TOO_LARGE": {
        "title": "Súbor je príliš veľký",
        "message": "Súbor je príliš veľký"
      },
      "PROCESSING_ERROR": {
        "title": "Chyba spracovania",
        "message": "Vyskytla sa chyba spracovania"
      },
      "NETWORK_ERROR": {
        "title": "Chyba siete",
        "message": "Problém s pripojením k sieti"
      },
      "SERVER_ERROR": {
        "title": "Chyba servera",
        "message": "Vyskytla sa chyba servera"
      }
    },
    "accountDeletion": {
      "LOAD_REQUESTS_FAILED": {
        "title": "Chyba",
        "message": "Nepodarilo sa načítať žiadosti o vymazanie. Skúste to prosím znova."
      },
      "CREATE_REQUEST_FAILED": {
        "title": "Chyba",
        "message": "Nepodarilo sa vytvoriť žiadosť o vymazanie. Skúste to prosím znova."
      },
      "CANCEL_REQUEST_FAILED": {
        "title": "Chyba",
        "message": "Nepodarilo sa zrušiť žiadosť o vymazanie. Skúste to prosím znova."
      },
      "DUPLICATE_REQUEST": {
        "title": "Duplicitná žiadosť",
        "message": "Žiadosť o vymazanie pre túto entitu už existuje."
      },
      "INVALID_PASSWORD": {
        "title": "Neplatné heslo",
        "message": "Zadané heslo je nesprávne."
      },
      "BAD_REQUEST": {
        "title": "Chybná žiadosť",
        "message": "Žiadosť sa nepodarilo spracovať. Skontrolujte prosím svoj vstup a skúste to znova."
      }
    }
  }
};

const errorsCz = {
  "errors": {
    "jobApply": {
      "JOB_ALREADY_APPLIED": {
        "title": "Již jste se přihlásili",
        "message": "Na tuto pozici jste se již přihlásili. Nemůžete se přihlásit vícekrát na stejnou pozici."
      },
      "JOB_OFFER_NOT_FOUND": {
        "title": "Pracovní nabídka nenalezena",
        "message": "Pracovní nabídka, o kterou se snažíte ucházet, již neexistuje nebo byla odstraněna."
      },
      "JOB_ALREADY_ASSIGNED_TO_CANDIDATE": {
        "title": "Již přiřazeno",
        "message": "Tato práce vám již byla přiřazena. Zkontrolujte prosím vaše přihlášky."
      },
      "NETWORK_ERROR": {
        "title": "Chyba sítě",
        "message": "Nepodařilo se připojit k serveru. Zkontrolujte prosím své internetové připojení a zkuste to znovu."
      },
      "INVALID_DATA": {
        "title": "Neplatné údaje",
        "message": "Poskytnuté informace jsou neplatné. Zkontrolujte prosím své vstupy a zkuste to znovu."
      },
      "UNAUTHORIZED": {
        "title": "Neautorizovaný přístup",
        "message": "Nemáte oprávnění provést tuto akci. Přihlaste se prosím a zkuste to znovu."
      },
      "INTERNAL_SERVER_ERROR": {
        "title": "Chyba serveru",
        "message": "Na serveru došlo k neočekávané chybě. Zkuste to prosím později."
      },
      "UNKNOWN_ERROR": {
        "title": "Došlo k chybě",
        "message": "Došlo k neočekávané chybě. Zkuste to prosím znovu nebo kontaktujte podporu, pokud problém přetrvává."
      }
    },
    "common": {
      "CONNECTION_ERROR": {
        "title": "Chyba připojení",
        "message": "Zkontrolujte prosím své internetové připojení."
      },
      "SESSION_EXPIRED": {
        "title": "Relace vypršela",
        "message": "Přihlaste se prosím znovu."
      },
      "GENERIC_ERROR": {
        "title": "Chyba",
        "message": "Došlo k chybě. Zkuste to prosím znovu."
      }
    },
    "jobOperations": {
      "SAVE_JOB_FAILED": {
        "title": "Chyba",
        "message": "Nepodařilo se uložit práci. Zkuste to prosím znovu."
      },
      "UNSAVE_JOB_FAILED": {
        "title": "Chyba",
        "message": "Nepodařilo se odstranit uloženou práci. Zkuste to prosím znovu."
      },
      "LOAD_JOBS_FAILED": {
        "title": "Chyba při načítání pracovních míst",
        "message": "Omlouváme se, nemohli jsme načíst pracovní nabídky. Zkuste to prosím později."
      },
      "LOAD_SAVED_JOBS_FAILED": {
        "title": "Chyba při načítání uložených pracovních míst",
        "message": "Nepodařilo se načíst uložené práce. Zkuste to prosím znovu."
      },
      "JOB_NOT_FOUND": {
        "title": "Pracovní nabídka nenalezena",
        "message": "Omlouváme se, nemohli jsme najít pracovní nabídku, kterou hledáte. Mohla být odstraněna nebo neexistuje."
      },
      "SAVE_JOB_AUTH_REQUIRED": {
        "title": "Vyžaduje se přihlášení",
        "message": "Musíte se přihlásit, abyste mohli uložit práci do svého profilu. Přihlaste se prosím nebo vytvořte účet."
      }
    },
    "auth": {
      "INVALID_CREDENTIALS": {
        "title": "Neplatné přihlašovací údaje",
        "message": "Neplatný e-mail nebo heslo"
      },
      "LOGIN_FAILED": {
        "title": "Přihlášení selhalo",
        "message": "Vyskytla se chyba při přihlašování"
      },
      "REGISTRATION_FAILED": {
        "title": "Registrace selhala",
        "message": "Vyskytla se chyba při registraci. Zkuste to prosím později."
      },
      "INVALID_RESET_TOKEN": {
        "title": "Neplatný token",
        "message": "Neplatný nebo expirovaný token. Požádejte prosím o nový odkaz na obnovení hesla."
      },
      "NO_RESET_TOKEN": {
        "title": "Žádný token",
        "message": "Nebyl poskytnut token na obnovení. Požádejte prosím o nový odkaz na obnovení hesla."
      },
      "PASSWORD_RESET_FAILED": {
        "title": "Obnovení selhalo",
        "message": "Vyskytla se chyba při obnovování hesla. Zkuste to prosím znovu."
      }
    },
    "profile": {
      "UPDATE_FAILED": {
        "title": "Aktualizace selhala",
        "message": "Vyskytl se problém při aktualizaci vašeho profilu. Zkuste to prosím znovu."
      }
    },
    "resumeParsing": {
      "PARSING_FAILED": {
        "title": "Analýza selhala",
        "message": "Nemohli jsme zpracovat váš životopis. Zkuste to prosím znovu."
      },
      "INVALID_FILE": {
        "title": "Neplatný soubor",
        "message": "Neplatný formát souboru"
      },
      "FILE_TOO_LARGE": {
        "title": "Soubor je příliš velký",
        "message": "Soubor je příliš velký"
      },
      "PROCESSING_ERROR": {
        "title": "Chyba zpracování",
        "message": "Vyskytla se chyba zpracování"
      },
      "NETWORK_ERROR": {
        "title": "Chyba sítě",
        "message": "Problém s připojením k síti"
      },
      "SERVER_ERROR": {
        "title": "Chyba serveru",
        "message": "Vyskytla se chyba serveru"
      }
    },
    "accountDeletion": {
      "LOAD_REQUESTS_FAILED": {
        "title": "Chyba",
        "message": "Nepodařilo se načíst žádosti o vymazání. Zkuste to prosím znovu."
      },
      "CREATE_REQUEST_FAILED": {
        "title": "Chyba",
        "message": "Nepodařilo se vytvořit žádost o vymazání. Zkuste to prosím znovu."
      },
      "CANCEL_REQUEST_FAILED": {
        "title": "Chyba",
        "message": "Nepodařilo se zrušit žádost o vymazání. Zkuste to prosím znovu."
      },
      "DUPLICATE_REQUEST": {
        "title": "Duplicitní žádost",
        "message": "Žádost o vymazání pro tuto entitu již existuje."
      },
      "INVALID_PASSWORD": {
        "title": "Neplatné heslo",
        "message": "Zadané heslo je nesprávné."
      },
      "BAD_REQUEST": {
        "title": "Chybná žádost",
        "message": "Žádost se nepodařilo zpracovat. Zkontrolujte prosím svůj vstup a zkuste to znovu."
      }
    }
  }
};

export const ERROR_TRANSLATIONS = {
  en: errorsEn,
  sk: errorsSk,
  cz: errorsCz
} as const;

/**
 * Helper function to get error translation key
 * @param errorCode - Backend error code (e.g., 'JOB_ALREADY_APPLIED', 'Network Error')
 * @param field - 'title' or 'message'
 * @param category - Error category: 'jobApply' | 'common' | 'jobOperations' | 'accountDeletion' | 'auth' | 'profile' | 'resumeParsing' (defaults to 'jobApply')
 * @returns Translation key string
 */
export function getErrorTranslationKey(
  errorCode: string,
  field: 'title' | 'message',
  category: 'jobApply' | 'common' | 'jobOperations' | 'accountDeletion' | 'auth' | 'profile' | 'resumeParsing' = 'jobApply'
): string {
  // Normalize error code
  const normalizedCode = normalizeErrorCode(errorCode);
  return `errors.${category}.${normalizedCode}.${field}`;
}

/**
 * Normalize backend error codes to match our translation keys
 */
function normalizeErrorCode(errorCode: string | undefined): string {
  if (!errorCode) {
    return 'UNKNOWN_ERROR';
  }

  // Map common error messages to our keys
  const errorMapping: Record<string, string> = {
    // Job Apply errors
    'JOB_ALREADY_APPLIED': 'JOB_ALREADY_APPLIED',
    'JOB_OFFER_NOT_FOUND': 'JOB_OFFER_NOT_FOUND',
    'JOB_ALREADY_ASSIGNED_TO_CANDIDATE': 'JOB_ALREADY_ASSIGNED_TO_CANDIDATE',

    // Common errors
    'Network Error': 'NETWORK_ERROR',
    'Invalid Data': 'INVALID_DATA',
    'Unauthorized': 'UNAUTHORIZED',
    'Internal Server Error': 'INTERNAL_SERVER_ERROR',
    'INTERNAL_SERVER_ERROR': 'INTERNAL_SERVER_ERROR',
    'CONNECTION_ERROR': 'CONNECTION_ERROR',
    'SESSION_EXPIRED': 'SESSION_EXPIRED',
    'GENERIC_ERROR': 'GENERIC_ERROR',

    // Job Operations errors
    'SAVE_JOB_FAILED': 'SAVE_JOB_FAILED',
    'UNSAVE_JOB_FAILED': 'UNSAVE_JOB_FAILED',
    'LOAD_JOBS_FAILED': 'LOAD_JOBS_FAILED',
    'LOAD_SAVED_JOBS_FAILED': 'LOAD_SAVED_JOBS_FAILED',
    'JOB_NOT_FOUND': 'JOB_NOT_FOUND',
    'SAVE_JOB_AUTH_REQUIRED': 'SAVE_JOB_AUTH_REQUIRED',

    // Auth errors
    'INVALID_CREDENTIALS': 'INVALID_CREDENTIALS',
    'LOGIN_FAILED': 'LOGIN_FAILED',
    'REGISTRATION_FAILED': 'REGISTRATION_FAILED',
    'INVALID_RESET_TOKEN': 'INVALID_RESET_TOKEN',
    'NO_RESET_TOKEN': 'NO_RESET_TOKEN',
    'PASSWORD_RESET_FAILED': 'PASSWORD_RESET_FAILED',

    // Profile errors
    'UPDATE_FAILED': 'UPDATE_FAILED',

    // Resume Parsing errors
    'PARSING_FAILED': 'PARSING_FAILED',
    'INVALID_FILE': 'INVALID_FILE',
    'FILE_TOO_LARGE': 'FILE_TOO_LARGE',
    'PROCESSING_ERROR': 'PROCESSING_ERROR',

    // Account Deletion errors
    'LOAD_REQUESTS_FAILED': 'LOAD_REQUESTS_FAILED',
    'CREATE_REQUEST_FAILED': 'CREATE_REQUEST_FAILED',
    'CANCEL_REQUEST_FAILED': 'CANCEL_REQUEST_FAILED',
    'DUPLICATE_REQUEST': 'DUPLICATE_REQUEST',
    'INVALID_PASSWORD': 'INVALID_PASSWORD',
    'BAD_REQUEST': 'BAD_REQUEST',
  };

  return errorMapping[errorCode] || 'UNKNOWN_ERROR';
}
